import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges, OnDestroy, AfterViewInit, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { PortalProductsService } from '../../services/portal-products.service';
import { AccountService } from '../../services/account.service';
import { Router } from '@angular/router';
import { IdleLogoutService } from '../../services/idle-logout.service';
import { GetPortalProductFromKey, GetPortalProductIdFromKey } from '../../data/portal-products';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
@Component({
  selector: 'app-portal-dashboard',
  templateUrl: './portal-dashboard.component.html',
  styleUrls: ['./portal-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalDashboardComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  currentUser = null;
  balanceDetails = null;
  balanceInProgress = false;
  accountFinBalances = null;
  totalBalanceOnCALS = 0;
  calsBalancesDetails = [];
  totalBalanceOnLEDGER = 0; // Margin Trading balance
  totalBalanceOnCALI = 0; // Fixed Income balance
  caliBalancesDetails = []; // Fixed Income balances array
  isCustomer = false;
  investorCode = null;
  productsStatus: { CALT: { status: '' }; CALS: {}; CALI: { status: '' } };
  inProgress = false;
  productInProgress;
  private resizeHandler: () => void;
  private langChangeSubscription: Subscription;
  vstockSlides: any[] = [];
  currentVstockSlideIndex: number = 0;
  private vstockAutoRotateInterval: any;
  private vstockPaused = false;
  // Set when a product's balance request fails, so the card can say so instead of showing nothing
  balanceErrors = { CALS: false, LEDGER: false, CALI: false };
  constructor(
    private portalProductService: PortalProductsService,
    private cdRef: ChangeDetectorRef,
    private accountService: AccountService,
    private route: Router,
    private idleLogoutService: IdleLogoutService,
    private elementRef: ElementRef,
    public translate: TranslateService,
  ) { }

  async ngOnInit() {
    this.inProgress = true;
    this.balanceInProgress = true; // Start balance loading for all products
    this.productInProgress = true;
    this.cdRef.detectChanges();

    try {
      this.currentUser = await this.accountService.currentUserAsync();
    } catch (e) {
      console.error('Error loading current user:', e);
    }
    this.isCustomer = this.currentUser?.priviledges?.some((prev) => prev === 'PRODUCT_SIGNUP') || false;
    this.investorCode = this.currentUser?.investorCode;
    this.inProgress = false;
    this.cdRef.detectChanges();

    // Initialize idle logout for portal dashboard
    this.idleLogoutService.initializeIdleLogout('portal-dashboard');

    // Subscribe to language changes to update translations
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.cdRef.detectChanges();
    });

    // Initialize VSTOCK slides
    this.initVstockSlides();
    this.startVstockAutoRotate();
    this.cdRef.detectChanges();

    // Balances and product statuses don't depend on each other, so load them together.
    // CALI is the exception: whether to fetch it depends on its product status.
    const statusesThenCali = this.loadProductStatuses().then(() =>
      this.isProductApproved('CALI') ? this.getFixedIncomeBalances() : undefined
    );
    await Promise.all([this.getCalsBalances(), this.getLedgerBalances(), statusesThenCali]);

    // Hide loader after all balances (including CALI) are fetched
    this.balanceInProgress = false;
    this.cdRef.detectChanges();
  }

  async loadProductStatuses() {
    try {
      // Ask the server each visit so approvals made since login show up here.
      // Match the same record the signup flow uses (productId), then the fargate product name.
      // A division-only match can attach another product's status to this card.
      const productStatuses = (await this.portalProductService.getProductStatuses()) || [];
      const find = (productKey: string, name: string) => {
        const productId = GetPortalProductIdFromKey(productKey);
        return (
          productStatuses.find((ps: any) => ps.productId == productId) ||
          productStatuses.find((ps: any) => ps.productName === name) ||
          ''
        );
      };
      this.productsStatus = {
        CALT: productStatuses.find((ps: any) => ps.productName === 'CALT-Bills') || '',
        CALS: find('equity', 'CALS-Equity'),
        CALI: find('fixed-income', 'CALI-Income Fund'),
      };
    } catch (e) {
      console.error('Error fetching product statuses:', e);
    }
    this.productInProgress = false;
    this.cdRef.detectChanges();
  }

  ngAfterViewInit() {
    // Equalize heights for cards in the same row
    setTimeout(() => {
      this.equalizeCardHeights();
    }, 100);

    // Recalculate heights on window resize
    this.resizeHandler = () => {
      this.equalizeCardHeights();
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  equalizeCardHeights() {
    const hostElement = this.elementRef.nativeElement;

    // Remove inline height styles from all cards
    const allCards = hostElement.querySelectorAll('.dashboard-card');
    allCards.forEach((card: HTMLElement) => {
      card.style.height = '';
    });
  }

  async getCalsBalances() {
    this.calsBalancesDetails = [];
    this.totalBalanceOnCALS = 0;
    const accounts = this.currentUser?.investorCodeDetails || [];
    if (!accounts.length) return;

    // Fetch every account at once; one failing account shouldn't hide the others
    const results = await Promise.allSettled(
      accounts.map((acc) => this.accountService.getAccountFinBalances(acc.customerCode))
    );
    results.forEach((result, i) => {
      const balance = result.status === 'fulfilled' ? result.value?.[0] : null;
      if (balance) {
        this.calsBalancesDetails.push(balance);
        // balance-summary returns the holding on `balance` (same field fargate and the equity page use)
        this.totalBalanceOnCALS += this.toAmount(balance.balance);
      } else {
        this.balanceErrors.CALS = true;
        console.error('Error fetching CALS balance for', accounts[i].customerCode,
          result.status === 'rejected' ? result.reason : 'empty response');
      }
    });
    this.totalBalanceOnCALS = this.roundMoney(this.totalBalanceOnCALS);
    this.cdRef.detectChanges();
  }

  // Sums of API amounts pick up float drift (1234.57 -> 1234.5699999); round back to paisa
  private roundMoney(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  // API amounts arrive as numbers or comma-formatted strings. Number("1,234.50") is NaN, which was showing as 0.
  private toAmount(value: any): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number(value.replace(/,/g, '').trim());
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  async getLedgerBalances() {
    this.totalBalanceOnLEDGER = 0;
    const accounts = this.currentUser?.marginTradeDetails || [];
    if (!accounts.length) return;

    // Local date, not UTC, so early-morning visits in Dhaka don't ask for yesterday's portfolio
    const today = moment().format('YYYY-MM-DD');

    // Always read today's portfolio from the API. A cached total can disagree with the live marketValueTotal.
    const results = await Promise.allSettled(
      accounts.map((margin) => this.accountService.getMarginTradePortfolio(today, margin.customerCode))
    );
    // Per-account totals are also read by the Margin Trading page (ledger.component)
    const marginTradeDetails = [];
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        const total = this.toAmount(result.value?.marketValueTotal);
        marginTradeDetails.push({ code: accounts[i].customerCode, total });
        this.totalBalanceOnLEDGER += total;
      } else {
        this.balanceErrors.LEDGER = true;
        console.error('Error fetching margin trading balance for', accounts[i].customerCode, result.reason);
      }
    });

    this.totalBalanceOnLEDGER = this.roundMoney(this.totalBalanceOnLEDGER);

    // Don't cache a partial total
    if (!this.balanceErrors.LEDGER) {
      localStorage.setItem('marginTradeTotal', this.totalBalanceOnLEDGER.toString());
      localStorage.setItem('marginTradeTotalDate', today);
      localStorage.setItem('marginTradeDetails', JSON.stringify(marginTradeDetails));
    }
    this.cdRef.detectChanges();
  }

  async getFixedIncomeBalances() {
    try {
      this.totalBalanceOnCALI = 0;
      this.caliBalancesDetails = [];

      // Check if user has signed up for CALI
      const caliStatus = this.getProductStatus('CALI');
      if (!caliStatus || caliStatus === 'Draft' || caliStatus === 'Approval Pending') {
        this.cdRef.detectChanges();
        return;
      }

      // cali-portfolio-summary is an array of funds. The holding is subAccounts[].balance_amount,
      // not fund.total / fund.balance (those fields are absent or are a different figure).
      try {
        const accountType = 'INDIVIDUAL';
        const summary = await this.accountService.getAccountSummary(
          this.currentUser?.crmId || this.currentUser?.id,
          accountType,
          true // displayAllFunds
        );
        const funds = Array.isArray(summary) ? summary : Array.isArray(summary?.data) ? summary.data : [];
        funds.forEach((fund: any) => {
          const subs = Array.isArray(fund?.subAccounts) ? fund.subAccounts : null;
          if (subs?.length) {
            subs.forEach((sub: any) => {
              const amount = this.toAmount(sub?.balance_amount);
              this.totalBalanceOnCALI += amount;
              this.caliBalancesDetails.push({ ...sub, balance: amount });
            });
          } else if (fund && (fund.balance_amount != null || fund.balance != null || fund.totalBalance != null)) {
            const amount = this.toAmount(fund.balance_amount ?? fund.balance ?? fund.totalBalance);
            this.totalBalanceOnCALI += amount;
            this.caliBalancesDetails.push(fund);
          }
        });
      } catch (e) {
        this.balanceErrors.CALI = true;
        console.error('Error fetching CALI portfolio summary:', e);
      }
      this.totalBalanceOnCALI = this.roundMoney(this.totalBalanceOnCALI);

      this.cdRef.detectChanges();
    } catch (e) {
      console.error('Error fetching Fixed Income balances:', e);
      this.cdRef.detectChanges();
    }
  }

  async getAccountFinBalances(profileId?) {
    try {
      this.balanceInProgress = true;
      this.accountFinBalances = await this.accountService.getAccountFinBalances(profileId);
      this.balanceInProgress = false;
      this.cdRef.detectChanges();
    } catch (e) {
      this.balanceInProgress = false;
      this.cdRef.detectChanges();
    }
  }

  navigateProduct(product) {
    // if (this.accountFinBalances && this.accountFinBalances.length > 0) {
    // if (product === 'CALS' && this.accountFinBalances && this.accountFinBalances.length > 0) {
    if (product === 'CALS') {
      if (this.currentUser?.investorCodeDetails?.length > 0) {
        this.route.navigate([`/dashboard/equity`], { queryParams: { uid: `${this.currentUser.id}` } });
      } else {
        this.route.navigate([`/products/equity`], { queryParams: { uid: `${this.currentUser.id}` } });
      }
    } else if (product === 'LEDGER') {
      // Margin Trading navigation
      if (this.currentUser?.marginTradeDetails?.length > 0) {
        this.route.navigate([`/dashboard/margin-trading`], { queryParams: { uid: `${this.currentUser.id}` } });
      } else {
        this.route.navigate([`/products/margin-trading`], { queryParams: { uid: `${this.currentUser.id}` } });
      }
    } else if (product === 'CALI') {
      // There's no /dashboard/fixed-income page yet, so Fixed Income always opens its product page
      this.route.navigate([`/products/fixed-income`], { queryParams: { uid: `${this.currentUser.id}` } });
    }
    // }
  }

  // Check if user has signed up for a product
  hasSignedUp(product: string): boolean {
    if (product === 'CALS') {
      return !!(this.currentUser?.investorCodeDetails?.length > 0);
    } else if (product === 'LEDGER') {
      // Margin Trading - check if user has margin trade details (accounts exist)
      return !!(this.currentUser?.marginTradeDetails?.length > 0);
    } else if (product === 'CALI') {
      const status = this.getProductStatus('CALI');
      return status === 'Approved' || this.caliBalancesDetails.length > 0;
    }
    return false;
  }

  // Check if form is partially completed (has status but not fully signed up)
  isPartiallyCompleted(product: string): boolean {
    if (product === 'CALS') {
      const status = this.getProductStatus('CALS');
      return !!(status && status !== 'Approval Pending' && !this.currentUser?.investorCodeDetails?.length);
    } else if (product === 'LEDGER') {
      return !!(this.currentUser?.marginTradeDetails?.length > 0 && this.totalBalanceOnLEDGER === 0);
    } else if (product === 'CALI') {
      const status = this.getProductStatus('CALI');
      return !!(status && status !== 'Approved' && status !== 'Approval Pending' && !this.hasBalance('CALI'));
    }
    return false;
  }

  // Get button text based on sign-up status
  getButtonText(product: string): string {
    if (this.hasSignedUp(product)) {
      return this.translate.currentLang === 'en' ? 'View Portfolio' : 'পোর্টফোলিও দেখুন';
    } else {
      return this.translate.currentLang === 'en' ? 'Sign Up' : 'সাইন আপ';
    }
  }

  getStatusClass(status: string): string {
    if (!status) return 'status-default';
    const statusMap = {
      'Draft': 'status-draft',
      'Submitted': 'status-submitted',
      'Re-Submitted': 'status-resubmitted',
      'Approval Pending': 'status-pending',
      'Returned': 'status-returned',
      'Rejected': 'status-rejected',
      'Approved': 'status-approved',
      'Modifying': 'status-modifying',
      'Awaiting CDS Approval': 'status-awaiting'
    };
    return statusMap[status] || 'status-default';
  }

  // Text for the action link under a product's application status
  getStatusActionText(status: string): string {
    const en = this.translate.currentLang === 'en';
    if (!status) return en ? 'Sign Up' : 'সাইন আপ';
    if (status === 'Approved') return en ? 'View Portfolio' : 'পোর্টফোলিও দেখুন';
    return en ? 'View Application' : 'আবেদন দেখুন';
  }

  getStatusText(status: string): string {
    if (!status) return this.translate.currentLang === 'en' ? 'Sign Up' : 'সাইন আপ';
    const isBengali = this.translate.currentLang === 'bn' || this.translate.currentLang === 'bn-BD';
    const statusMap = {
      'Draft': isBengali ? 'খসড়া' : 'Draft',
      'Submitted': isBengali ? 'জমা দেওয়া হয়েছে' : 'Submitted',
      'Re-Submitted': isBengali ? 'পুনরায় জমা দেওয়া হয়েছে' : 'Re-Submitted',
      'Approval Pending': isBengali ? 'অনুমোদন মুলতুবি' : 'Approval Pending',
      'Returned': isBengali ? 'ফেরত দেওয়া হয়েছে' : 'Returned',
      'Rejected': isBengali ? 'প্রত্যাখ্যান' : 'Rejected',
      'Approved': isBengali ? 'অনুমোদিত' : 'Approved',
      'Modifying': isBengali ? 'সম্পাদনা করা হচ্ছে' : 'Modifying',
      'Awaiting CDS Approval': isBengali ? 'সিডিএস অনুমোদনের অপেক্ষায়' : 'Awaiting CDS Approval'
    };
    return statusMap[status] || status;
  }

  // Get product status from productsStatus object
  getProductStatus(product: string): string | undefined {
    const statusObj = product === 'CALS' ? this.productsStatus?.CALS :
      product === 'CALI' ? this.productsStatus?.CALI : null;

    if (!statusObj || (typeof statusObj === 'string' && statusObj === '')) {
      return undefined;
    }

    if (typeof statusObj === 'object' && 'status' in statusObj) {
      const status = (statusObj as any).status;
      return (status && status !== '') ? status : undefined;
    }

    return undefined;
  }

  // Check if product has a status (not undefined, not empty, not 'Approved')
  hasStatus(product: string): boolean {
    const status = this.getProductStatus(product);
    return !!(status && status !== 'Approved');
  }

  // Check if user has a specific account type for a product
  hasAccountType(product: string, accountType: string): boolean {
    if (product === 'CALS') {
      if (!this.currentUser?.investorCodeDetails?.length) {
        return false;
      }
      if (accountType === 'individual') {
        // First account is typically individual
        return this.currentUser.investorCodeDetails.length > 0;
      } else if (accountType === 'joint') {
        // Additional accounts are typically joint
        return this.currentUser.investorCodeDetails.length > 1;
      } else if (accountType === 'corporate') {
        // Check if any account is corporate (you may need to adjust based on your data structure)
        return this.currentUser.investorCodeDetails.some((acc: any) =>
          acc.accountType === 'corporate' || acc.accountHolderType === 'corporate'
        );
      }
    } else if (product === 'LEDGER') {
      if (!this.currentUser?.marginTradeDetails?.length) {
        return false;
      }
      if (accountType === 'individual') {
        // First account is typically individual
        return this.currentUser.marginTradeDetails.length > 0;
      } else if (accountType === 'joint') {
        // Additional accounts are typically joint
        return this.currentUser.marginTradeDetails.length > 1;
      } else if (accountType === 'corporate') {
        // Check if any account is corporate
        return this.currentUser.marginTradeDetails.some((acc: any) =>
          acc.accountType === 'corporate' || acc.accountHolderType === 'corporate'
        );
      }
    } else if (product === 'CALI') {
      // For Fixed Income, check if user has signed up
      // You may need to adjust this based on your CALI data structure
      if (!this.hasSignedUp('CALI')) {
        return false;
      }
      // Default to individual if signed up (adjust based on your data)
      return accountType === 'individual';
    }
    return false;
  }

  // Get total net worth (sum of all balances)
  getTotalNetWorth(): number {
    return this.balanceOf('CALS') + this.balanceOf('LEDGER') + this.balanceOf('CALI');
  }

  private balanceOf(product: string): number {
    if (product === 'CALS') return this.totalBalanceOnCALS || 0;
    if (product === 'LEDGER') return this.totalBalanceOnLEDGER || 0;
    if (product === 'CALI') return this.totalBalanceOnCALI || 0;
    return 0;
  }

  // True share of net worth: this product's API balance / (Equities + Margin Trading + Fixed Income)
  private shareOf(product: string): number {
    const total = this.getTotalNetWorth();
    if (total <= 0) return 0;
    return (this.balanceOf(product) / total) * 100;
  }

  // Bar uses the exact share so its width matches the balance
  getAllocationPercentage(product: string): number {
    return this.shareOf(product);
  }

  // One decimal so 99.4 stays 99.4, instead of being pushed to 100 to force a round total
  getAllocationLabel(product: string): string {
    const share = this.shareOf(product);
    if (share > 0 && share < 0.1) return '<0.1%';
    const rounded = Math.round((share + Number.EPSILON) * 10) / 10;
    return `${rounded.toFixed(1)}%`;
  }

  // Only products with an API balance. Highest share first.
  getSortedAllocationItems(): any[] {
    const items = [
      {
        product: 'CALS',
        nameEn: 'Equities',
        nameBn: 'ইকুইটি',
        color: '#6B8ED6'
      },
      {
        product: 'LEDGER',
        nameEn: 'Margin Trading',
        nameBn: 'মার্জিন ট্রেডিং',
        color: '#2F5FB3'
      },
      {
        product: 'CALI',
        nameEn: 'Fixed Income',
        nameBn: 'ফিক্সড ইনকাম',
        color: '#0B2F6B'
      }
    ];

    return items
      .filter((item) => this.hasBalance(item.product))
      .sort((a, b) => this.balanceOf(b.product) - this.balanceOf(a.product));
  }

  // Check if product has actual balance (not just signed up)
  hasBalance(product: string): boolean {
    if (product === 'CALS') {
      return (this.totalBalanceOnCALS || 0) > 0;
    } else if (product === 'LEDGER') {
      return (this.totalBalanceOnLEDGER || 0) > 0;
    } else if (product === 'CALI') {
      return (this.totalBalanceOnCALI || 0) > 0;
    }
    return false;
  }

  // Check if any product has balance or is signed up (to show asset allocation card)
  hasAnyProductValue(): boolean {
    return this.hasBalance('CALS') || this.hasBalance('LEDGER') || this.hasBalance('CALI') ||
      this.hasSignedUp('CALS') || this.hasSignedUp('LEDGER') || this.hasSignedUp('CALI');
  }

  // Check if a specific product is approved (not in draft/pending state)
  isProductApproved(product: string): boolean {
    if (product === 'CALS') {
      // For CALS, check if user has investor code AND status is not pending
      const hasInvestorCode = !!(this.currentUser?.investorCodeDetails?.length > 0);
      if (!hasInvestorCode) return false;

      const calsStatus = this.getProductStatus('CALS');
      // Approved if status is undefined (fully signed up) or status exists and is not "Approval Pending"
      return !calsStatus || calsStatus !== 'Approval Pending';
    } else if (product === 'LEDGER') {
      // For LEDGER, check if user has margin trade details and balance
      return !!(this.currentUser?.marginTradeDetails?.length > 0 && this.totalBalanceOnLEDGER > 0);
    } else if (product === 'CALI') {
      // For CALI, check if status exists and is not "Approval Pending"
      const caliStatus = this.getProductStatus('CALI');
      return !!(caliStatus && caliStatus !== 'Approval Pending');
    }
    return false;
  }

  // Check if user is approved (has at least one product that is not in "Approval Pending" status)
  isUserApproved(): boolean {
    // Check if user has balances (which means they're approved and can use actions)
    const hasBalance = this.hasBalance('CALS') || this.hasBalance('LEDGER') || this.hasBalance('CALI');
    if (hasBalance) {
      return true;
    }

    // Check if user has any product that is approved (not pending)
    if (this.productsStatus) {
      const calsStatus = this.getProductStatus('CALS');
      const caliStatus = this.getProductStatus('CALI');

      // User is approved if at least one product has a status that is not "Approval Pending"
      const hasApprovedProduct =
        (calsStatus && calsStatus !== 'Approval Pending') ||
        (caliStatus && caliStatus !== 'Approval Pending');

      // Also check if user has signed up for margin trading (LEDGER doesn't have status in productsStatus)
      const hasMarginTrading = this.hasSignedUp('LEDGER');

      return hasApprovedProduct || hasMarginTrading;
    }

    // If no product status yet, check if user has signed up for any product
    return this.hasSignedUp('CALS') || this.hasSignedUp('LEDGER') || this.hasSignedUp('CALI');
  }

  ngOnChanges(changes: SimpleChanges) { }

  initVstockSlides() {
    this.vstockSlides = [
      {
        titleEn: 'Practice Trading with VStock',
        titleBn: 'VStock-এর সাথে ট্রেডিং অনুশীলন করুন',
        descriptionEn: 'Practice trading with VStock, CAL\'s virtual stock simulator. Get BDT 1,000,000 in virtual funds, trade real prices, and climb the leaderboard — no real risk, just learning.',
        descriptionBn: 'VStock, CAL-এর ভার্চুয়াল স্টক সিমুলেটরের সাথে ট্রেডিং অনুশীলন করুন। ভার্চুয়াল তহবিলে BDT 1,000,000 পান, আসল দামে ট্রেড করুন এবং লিডারবোর্ডে উঠুন — কোনও আসল ঝুঁকি নেই, শুধু শেখা।',
        buttonEn: 'Let\'s play',
        buttonBn: 'চলুন খেলি',
        image: '/assets/images/banner/1.png'
      },
      {
        titleEn: 'Learn the Market',
        titleBn: 'বাজার শিখুন',
        descriptionEn: 'Master stock trading with real-time market data. Practice buying and selling stocks without any financial risk. Perfect for beginners and experienced traders alike.',
        descriptionBn: 'রিয়েল-টাইম বাজার ডেটা দিয়ে স্টক ট্রেডিং আয়ত্ত করুন। কোনও আর্থিক ঝুঁকি ছাড়াই স্টক কেনা-বেচা অনুশীলন করুন। শুরু এবং অভিজ্ঞ ট্রেডার উভয়ের জন্যই উপযুক্ত।',
        buttonEn: 'Start Learning',
        buttonBn: 'শেখা শুরু করুন',
        image: '/assets/images/banner/2.png'
      },
      {
        titleEn: 'Climb the Leaderboard',
        titleBn: 'লিডারবোর্ডে উঠুন',
        descriptionEn: 'Compete with other traders and see how you rank. Track your progress, improve your skills, and become a top trader in the VStock community.',
        descriptionBn: 'অন্যান্য ট্রেডারদের সাথে প্রতিযোগিতা করুন এবং আপনার র‍্যাঙ্ক দেখুন। আপনার অগ্রগতি ট্র্যাক করুন, আপনার দক্ষতা উন্নত করুন এবং VStock সম্প্রদায়ের শীর্ষ ট্রেডার হয়ে উঠুন।',
        buttonEn: 'View Rankings',
        buttonBn: 'র‍্যাঙ্কিং দেখুন',
        image: '/assets/images/banner/3.png'
      }
    ];
  }

  goToVstockSlide(index: number) {
    if (index === this.currentVstockSlideIndex) return;
    // Trigger animation by temporarily resetting
    this.currentVstockSlideIndex = -1;
    this.cdRef.detectChanges();
    setTimeout(() => {
      this.currentVstockSlideIndex = index;
      this.restartVstockAutoRotate();
      this.cdRef.detectChanges();
    }, 50);
  }

  startVstockAutoRotate() {
    this.stopVstockAutoRotate();
    // Don't rotate while the user is reading/interacting, or if they prefer reduced motion
    if (this.vstockPaused || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    this.vstockAutoRotateInterval = setInterval(() => {
      // Trigger animation
      const nextIndex = (this.currentVstockSlideIndex + 1) % this.vstockSlides.length;
      this.currentVstockSlideIndex = -1;
      this.cdRef.detectChanges();
      setTimeout(() => {
        this.currentVstockSlideIndex = nextIndex;
        this.cdRef.detectChanges();
      }, 50);
    }, 5000); // 5 seconds
  }

  stopVstockAutoRotate() {
    if (this.vstockAutoRotateInterval) {
      clearInterval(this.vstockAutoRotateInterval);
      this.vstockAutoRotateInterval = null;
    }
  }

  pauseVstockAutoRotate() {
    this.vstockPaused = true;
    this.stopVstockAutoRotate();
  }

  resumeVstockAutoRotate() {
    this.vstockPaused = false;
    this.startVstockAutoRotate();
  }

  restartVstockAutoRotate() {
    this.stopVstockAutoRotate();
    this.startVstockAutoRotate();
  }

  get currentVstockSlide() {
    if (!this.vstockSlides || this.vstockSlides.length === 0) {
      return null;
    }
    if (this.currentVstockSlideIndex < 0 || this.currentVstockSlideIndex >= this.vstockSlides.length) {
      // If index is invalid, default to first slide
      if (this.vstockSlides.length > 0) {
        this.currentVstockSlideIndex = 0;
      } else {
        return null;
      }
    }
    const slide = this.vstockSlides[this.currentVstockSlideIndex];
    if (slide) {
      const isBengali = this.translate.currentLang === 'bn' || this.translate.currentLang === 'bn-BD';
      return {
        ...slide,
        title: isBengali ? slide.titleBn : slide.titleEn,
        description: isBengali ? slide.descriptionBn : slide.descriptionEn,
        button: isBengali ? slide.buttonBn : slide.buttonEn
      };
    }
    return slide;
  }

  ngOnDestroy() {
    // Disable idle logout when component is destroyed
    this.idleLogoutService?.disableIdleLogout();

    // Remove resize event listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }

    // Unsubscribe from language changes
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }

    // Stop VSTOCK auto-rotate
    this.stopVstockAutoRotate();
  }
}
